<script setup lang="ts">
import axios from 'axios'
import type { PartnerParameter, Tablet } from '@/types/types'
const notification = useNotification()
const { t } = useI18n()

const props = defineProps<{
  partnerParameter: PartnerParameter
  storeId: number
  tablet?: Tablet
}>()

const isLoading = ref(true)
const isLoadingAssociation = ref(false)
const partnerIntegrations = ref(null)
const selectedPartnerIntegrationId = ref(props.tablet?.partnerDevice?.id)
const selectedPartnerDeviceId = ref(null)
const newPartnerDeviceId = ref(null)
const newDeviceName = ref(null)
const hasError = ref(false)

const formattedPartnerIntegrations = computed(() => {
  return partnerIntegrations.value?.map((device) => {
    return {
      ...device,
      text: formatDeviceText(device),
      disabled: device.tabletId !== null
    }
  })
})

const partnerIntegrationOptions = computed(() =>
  (formattedPartnerIntegrations.value ?? []).map((device) => ({
    value: String(device.id),
    label: device.text,
    disabled: device.disabled
  }))
)

const emit = defineEmits(['onDeviceAssociated'])

const formatDeviceText = (device) => {
  const deviceName = device.partnerDeviceName ?? `[${t('pages.marketplace.magicScanPage.modal.unnamedCheckout')}]`
  const isDeviceAvailable = device.tabletId === null
  return `${device.partnerDeviceId} - ${deviceName} ${
    !isDeviceAvailable ? ` (${t('pages.marketplace.magicScanPage.modal.unavailableCheckout')})` : ''
  }`
}

const getPartnerIntegrations = async () => {
  try {
    const { data } = await axios.get(
      `/partners/${props.partnerParameter.partner.id}/devices?storeId=${props.storeId}&accountId=${props.partnerParameter.id}`
    )
    partnerIntegrations.value = data.data
  } catch (error) {
    if (error?.response?.status === 500 || error?.response?.status === 401) {
      hasError.value = true
    } else if (error?.response?.status === 404) {
      partnerIntegrations.value = []
    }
  } finally {
    isLoading.value = false
  }
}

const createDevice = async () => {
  try {
    const { data } = await axios.post(`/partners/${props.partnerParameter.partner.id}/devices`, {
      partnerDeviceId: newPartnerDeviceId.value,
      partnerDeviceName: newDeviceName.value,
      storeId: props.storeId
    })
    newPartnerDeviceId.value = null
    newDeviceName.value = null
    partnerIntegrations.value.push(data.data)
    selectedPartnerIntegrationId.value = data.data.id
    selectedPartnerDeviceId.value = data.data.partnerDeviceId
  } catch (error) {
    throw new Error(error)
  }
}

const integrate = async () => {
  isLoadingAssociation.value = true

  try {
    await axios.post('/partner-integrations', {
      partnerDeviceId: selectedPartnerDeviceId.value.toString(),
      storeId: props.storeId,
      partnerParameterId: props.partnerParameter.id,
      tabletId: props.tablet.id,
      isActive: 1
    })
    notification.create('success', {
      text: t(`pages.marketplace.posSelector.tabletAssociationSuccess`),
      timeout: 3000
    })
    isLoadingAssociation.value = false
    emit('onDeviceAssociated')
  } catch (error) {
    notification.create('error', {
      text: t(`pages.marketplace.posSelector.tabletAssociationError`),
      timeout: 3000
    })
    throw new Error(error)
  }
}

const associatedTabletId = computed(() => {
  if (!selectedPartnerIntegrationId.value) {
    return
  }
  const partnerIntegration = partnerIntegrations.value?.find((d) => d.id === selectedPartnerIntegrationId.value)
  return partnerIntegration?.tabletId ?? null
})

const selectPartnerIntegration = (partnerIntegrationId) => {
  const partnerIntegration = partnerIntegrations.value?.find((d) => d.id === partnerIntegrationId)
  selectedPartnerDeviceId.value = partnerIntegration.partnerDeviceId
}

const selectedPartnerIntegrationIdString = computed({
  get: () => (selectedPartnerIntegrationId.value != null ? String(selectedPartnerIntegrationId.value) : ''),
  set: (v: string) => {
    const numVal = v ? Number(v) : null
    selectedPartnerIntegrationId.value = numVal
    if (numVal != null) selectPartnerIntegration(numVal)
  }
})

onMounted(async () => {
  await getPartnerIntegrations()
})

defineExpose({ integrate, selectedDevicePartnerDeviceId: selectedPartnerDeviceId })
</script>

<template>
  <div v-if="!isLoading" class="inline-block w-full">
    <div v-if="partnerIntegrations" class="space-y-8">
      <div class="text-left">
        <ComboboxSelect
          v-model="selectedPartnerIntegrationIdString"
          :options="partnerIntegrationOptions"
          :placeholder="t('pages.marketplace.magicScanPage.modal.selectCheckout')"
        />
        <span v-if="associatedTabletId && associatedTabletId !== tablet?.id" class="text-sm text-red-600">{{
          t(`pages.marketplace.posSelector.tabletUnavailableInfo`, { associationId: associatedTabletId })
        }}</span>
      </div>
      <div class="mb-2">
        <BaseDisclosure :is-default-open="false" :has-border="false" class="mb-16 w-full" title-bg-color="!bg-muted">
          <template #title>
            <div class="flex flex-col">
              <p class="block w-full text-sm font-semibold text-foreground-secondary">
                {{ t('pages.marketplace.magicScanPage.modal.notFoundCheckout.title') }}
              </p>
              <span class="block text-left text-sm text-muted-foreground">
                {{ t('pages.marketplace.magicScanPage.modal.notFoundCheckout.subtitle') }}
              </span>
            </div>
          </template>
          <form @submit.prevent="createDevice">
            <div class="m-4 grid grid-cols-3 gap-4 text-center text-xs">
              <AppFormInput
                id="newPartnerDeviceId"
                v-model="newPartnerDeviceId"
                :label="`${t('pages.marketplace.magicScanPage.partnerDeviceId')}*`"
                type="text"
                placeholder="123456"
              />
              <AppFormInput
                id="posDeviceName"
                v-model="newDeviceName"
                :label="t('pages.marketplace.magicScanPage.modal.checkoutName')"
                type="text"
                :placeholder="t('pages.marketplace.magicScanPage.modal.checkoutNamePlaceholder')"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                class="flex h-fit w-fit self-end"
                :disabled="!newPartnerDeviceId"
                @click="createDevice"
              >
                <BaseIcon name="HeroPlusIconSolid" />
              </Button>
            </div>
          </form>
        </BaseDisclosure>
      </div>
    </div>

    <BaseTip v-if="hasError" severity="error">
      <template #text>
        <p>
          {{ t(`pages.marketplace.posSelector.tabletConfigurationErrorTitle`) }}
        </p>
        <p class="my-2 text-muted-foreground">
          {{ t('pages.tablet.oauth.configurationErrorDescription', { name: partnerParameter?.partner?.name }) }}
        </p>
        <router-link :to="{ name: 'IntegrationList' }">
          <Button variant="outline">{{ t('pages.tablet.oauth.backToMarketplace') }}</Button>
        </router-link>
      </template>
    </BaseTip>
  </div>
  <AppSkeleton v-else type="section" />
</template>
